import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SlotImage from '@/components/base/SlotImage';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import type { ImpactStat } from '@/lib/types';

const FALLBACK_STATS: ImpactStat[] = [
  { label: 'Lives Transformed', value: 500, suffix: '+' },
  { label: 'Meals Distributed', value: 2000, suffix: '+' },
  { label: 'Students Sponsored', value: 150, suffix: '+' },
  { label: 'Homes Built', value: 30, suffix: '+' },
];

function Counter({ target, suffix }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const animated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true;
          const duration = 2000;
          const startTime = performance.now();
          const update = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const ease = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(ease * target));
            if (progress < 1) requestAnimationFrame(update);
          };
          requestAnimationFrame(update);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref}>
      <span className="font-heading text-5xl md:text-6xl font-bold text-white">
        {count.toLocaleString()}{suffix || ''}
      </span>
    </div>
  );
}

export default function ImpactStats() {
  const { data: settings } = useSiteSettings();
  const stats = settings?.impact_stats?.length ? settings.impact_stats : FALLBACK_STATS;

  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0">
        <SlotImage
          slot="impact_image"
          fallback="https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/7dd190bd-a977-4941-b5f7-34b8b48612b1_compressed_WhatsApp-Image-2026-09-12-at-15.29.02-3.webp"
          alt="Church community"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-primary-900/85" />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-12 lg:gap-20">
          <div className="lg:w-2/5">
            <p className="text-accent-400 font-label text-sm tracking-widest uppercase font-semibold mb-3">
              Our Impact
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white leading-tight mb-6">
              Transforming Lives Through Faith &amp; Service
            </h2>
            <div className="w-12 h-1 bg-accent-500 mb-6" />
            <p className="text-white/70 text-base leading-relaxed mb-8">
              Together, we are transforming lives through dedicated service, outreach programs, and youth empowerment initiatives that foster hope and change within our community.
            </p>
            <div className="flex items-center gap-3">
              <Link to="/about" className="btn-accent">
                <i className="ri-arrow-right-line" aria-hidden="true" />
                Our Story
              </Link>
              <Link to="/give" className="btn-outline-white">
                Support TGMCI
              </Link>
            </div>
          </div>

          <div className="lg:w-3/5 grid grid-cols-2 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center p-6 border border-white/10 rounded-xl bg-white/5 backdrop-blur-sm">
                <Counter target={stat.value} suffix={stat.suffix} />
                <p className="text-white/60 font-label text-sm uppercase tracking-widest mt-3">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}